import { useIsFocused } from '@react-navigation/native'
import { Link } from 'expo-router'
import IntlMessageFormat from 'intl-messageformat'
import { useEffect, useRef, type ReactNode } from 'react'

import { env } from '@/src/config/env'
import { DONATION_SOURCE } from '@/src/features/donation/api/types/donation'
import { useDonateLaterReminderMutation } from '@/src/features/donation/services/useDonateLaterReminderMutation'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { TEST_ID } from '@/src/general/constants/testId'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { storage } from '@/src/general/utilities/storage'
import { STORAGE_KEYS } from '@/src/general/utilities/storage/MMKVStorage'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants'
import { getKey, htmlTagsMap } from '@/src/lang/useTranslate'

import { DesktopDonationBanner } from './DesktopDonationBanner'
import type { TDonationBannerProps } from './DonationBanner.types'
import { MobileDonationBanner } from './MobileDonationBanner'

const createDonationHtmlTags = (prayingForHozanaFormUrl?: string | null) => {
  return {
    ...htmlTagsMap,
    a: (chunks: ReactNode[]) =>
      prayingForHozanaFormUrl ? (
        <Link
          key={getKey(chunks, 'a')}
          className="underline"
          testID={TEST_ID.DONATION.CAMPAIGN_LINK}
          href={prayingForHozanaFormUrl}
        >
          {chunks}
        </Link>
      ) : (
        chunks
      ),
  }
}

export const DonationBanner = ({
  donationData,
  donationSourceRef,
  closeDonationBanner,
  goToDonatePage,
  showDonationBanner,
}: TDonationBannerProps): ReactNode => {
  const bannerHasBeenDisplayed = useRef(false)
  const { lessThan } = useDimensions()
  const isFocused = useIsFocused()
  const donationHtmlTags = createDonationHtmlTags(donationData?.prayingForHozanaFormUrl)

  const donateLaterReminderMutation = useDonateLaterReminderMutation()

  useEffect(() => {
    if (!bannerHasBeenDisplayed.current && !env.EXPO_PUBLIC_BLOCK_BANNER && isFocused && showDonationBanner) {
      bannerHasBeenDisplayed.current = true
      GTM.sendEvent({
        event:
          donationSourceRef.current === DONATION_SOURCE.AUTOMATIC
            ? EVENTS.AUTO_BANNER_DISPLAYED
            : EVENTS.DON_BANNER_DISPLAYED,
      })
    }
  }, [donationSourceRef, isFocused, showDonationBanner])

  const donationBannerText = donationData?.text?.replace(/<!--[\s\S]*?-->\n/g, '') || ''
  const donationBannerNotes = donationData?.notes?.replace(/<!--[\s\S]*?-->\n/g, '') || ''
  const donationBannerSignature = donationData?.signature?.replace(/<!--[\s\S]*?-->\n/g, '') || ''

  let mainMessage: ReactNode = ''
  let notes: ReactNode = ''
  let signature: ReactNode = ''
  try {
    mainMessage = new IntlMessageFormat(donationBannerText).format<ReactNode>(donationHtmlTags)
    notes = new IntlMessageFormat(donationBannerNotes).format(donationHtmlTags)
    signature = new IntlMessageFormat(donationBannerSignature).format(donationHtmlTags)
  } catch {
    return <ErrorMessage defaultErrorKey="common.error.unknown" />
  }

  const messages = Array.isArray(mainMessage) ? mainMessage : [mainMessage]

  const handleClose = () => {
    GTM.sendEvent({
      event:
        donationSourceRef.current === DONATION_SOURCE.AUTOMATIC ? EVENTS.AUTO_BANNER_CLOSED : EVENTS.DON_BANNER_CLOSED,
    })
    bannerHasBeenDisplayed.current = false
    closeDonationBanner()
  }

  const handleDonate = () => {
    storage.removeItem(STORAGE_KEYS.DONATION_BANNER_NOT_BEEN_DISMISSED)

    goToDonatePage()

    GTM.sendEvent({
      event:
        donationSourceRef.current === DONATION_SOURCE.AUTOMATIC
          ? EVENTS.AUTO_BANNER_FORM_SUBMITTED
          : EVENTS.DON_FORM_SUBMITTED,
    })
  }

  const handleDonateLater = () => {
    GTM.sendEvent({
      event:
        donationSourceRef.current === DONATION_SOURCE.AUTOMATIC
          ? EVENTS.AUTO_BANNER_DONATE_LATER_CLICKED
          : EVENTS.DON_DONATE_LATER_CLICKED,
    })
    donateLaterReminderMutation.mutate({
      source: donationSourceRef.current,
      campaign: donationData.campaign?.key,
    })
    bannerHasBeenDisplayed.current = false
    closeDonationBanner()
  }

  if (lessThan.md)
    return (
      <MobileDonationBanner
        showDonationBanner={showDonationBanner}
        donationData={donationData}
        messages={messages}
        notes={notes}
        signature={signature}
        onClose={handleClose}
        onDonate={handleDonate}
        onDonateLater={handleDonateLater}
      />
    )
  // The mobile banner stays mounted so the modal can close itself; the desktop one is simply unmounted.
  if (!showDonationBanner) return null

  return (
    <DesktopDonationBanner
      donationData={donationData}
      messages={messages}
      notes={notes}
      signature={signature}
      onClose={handleClose}
      onDonate={handleDonate}
      onDonateLater={handleDonateLater}
    />
  )
}
